import { HttpClient } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';
import { buildInviteFailureState } from '../../../Shared Components/Utilities/utils';

@Component({
    selector: 'app-zentra-admin-integrator-invite-dialog',
    templateUrl: './zentra-admin-integrator-invite-dialog.component.html',
    styleUrls: ['./zentra-admin-integrator-invite-dialog.component.scss'],
    standalone: false
})
export class ZentraAdminIntegratorInviteDialogComponent {
  integratorEmail: string = '';
  emailForm!: FormGroup;
  successMessage: string = '';
  inviteErrorMessage: string = '';
  failedEmails: string[] = [];
  failedEmailMessages: string[] = [];
  constructor(
    public dialogRef: MatDialogRef<ZentraAdminIntegratorInviteDialogComponent>,
    readonly formBuilder: FormBuilder,
    readonly http: HttpClient,
    readonly backend: BackendAccessService,
    readonly snackBar: MatSnackBar
  ) {}
  /* istanbul ignore next */
  ngOnInit(): void {
    this.emailForm = this.formBuilder.group({
      emails: this.formBuilder.array([this.createEmailControl()]),
    });
  }
  /* istanbul ignore next */
  get emails(): FormArray {
    return this.emailForm.get('emails') as FormArray;
  }
  /* istanbul ignore next */
  createEmailControl(): FormGroup {
    return this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      role: ['Integrator', Validators.required],
    });
  }
  /* istanbul ignore next */
  selectRole(index: number, role: 'Integrator' | 'Wholesaler'): void {
    this.emails.at(index).get('role')?.setValue(role);
  }
  /* istanbul ignore next */
  addEmail(): void {
    this.emails.push(this.createEmailControl());
    this.inviteErrorMessage = ''; // Clear error when adding new email
    this.failedEmails = [];
    this.failedEmailMessages = [];
  }
  /* istanbul ignore next */
  removeEmail(index: number): void {
    if (this.emails.length > 1) {
      this.emails.removeAt(index);
      this.inviteErrorMessage = ''; // Clear error when removing email
      this.failedEmails = [];
      this.failedEmailMessages = [];
    }
  }
  /* istanbul ignore next */
  inviteIntegrators() {
    console.log('Invite Integrator');
    this.inviteErrorMessage = ''; // Clear any previous errors
    this.failedEmails = [];
    this.failedEmailMessages = [];
    if (this.emailForm.valid) {
      const roleCodes: Record<string, string> = {
        Integrator: 'ZINT',
        Wholesaler: 'ZWHL',
      };
      const users = this.emails.controls.map((control) => ({
        emailAddress: control.get('email')?.value,
        roleCode: roleCodes[control.get('role')?.value],
      }));

      const payload = { users };

      this.backend.sendInvite(payload).subscribe({
        next: (response: any) => {
          // Check if the response indicates a failure
          if (response?.isSuccess === false && response?.errors) {
            // Parse failed emails and use the API's own error message for each
            const failureState = buildInviteFailureState(response.errors);
            this.failedEmails = failureState.failedEmails;
            this.failedEmailMessages = failureState.failedEmailMessages;
            this.inviteErrorMessage = failureState.inviteErrorMessage;

            // Preserve the originally-selected role for each failed email before rebuilding
            const roleByEmail = new Map<string, string>(
              this.emails.controls.map((control) => [
                control.get('email')?.value,
                control.get('role')?.value,
              ])
            );

            // Remove successful email controls, keep only failed ones
            while (this.emails.length > 0) {
              this.emails.removeAt(0);
            }
            this.failedEmails.forEach(email => {
              const control = this.createEmailControl();
              control.get('email')?.setValue(email);
              control
                .get('role')
                ?.setValue(roleByEmail.get(email) ?? 'Integrator');
              this.emails.push(control);
            });
          } else {
            console.log('Invite successful:', response);
            this.snackBar.open('Invitation(s) sent successfully!', 'Close', {
              duration: 5000,
              panelClass: ['success-snackbar'],
            });
            this.dialogRef.close(true);
          }
        },
        error: (error) => {
          console.error('Error inviting integrators:', error);
          this.inviteErrorMessage =
            error?.error?.message ||
            'An error occurred while sending invitations. Please try again.';
        },
      });
    }
  }
}
